import { useState, useEffect, useRef } from "react"
import { User } from "../utils/interfaces"
import ErrorIcon from "shared/components/icons/error_icon"
import classnames from "classnames"
import parse from "html-react-parser"

import styles from "./intro.module.scss"
import arrowRightIcon from "images/icons/icon-arrow-right.svg?raw"

interface Props {
  active: boolean
  user: User
  onSubmit: (prompt: string) => void
}

const examplePrompts = [
  "If there's a new Tweet with keyword, then add to sheets.",
  "If I miss a phone call, then send myself an email.",
  "If an RSS feed has a new item, send me a notification.",
]

const Intro = ({ active, user, onSubmit }: Props) => {
  const [prompt, setprompt] = useState("")
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [errorMessage, updateErrorMessage] = useState("")

  useEffect(() => {
    textAreaRef?.current?.focus?.()
  }, [active])

  const onContinue = () => {
    const message: string = textAreaRef.current?.value || ""

    if (user.tier === "free") {
      window.App.Utils?.logCustomDatadogAction?.("free_user_ai_prompt", { message })
    }

    updateErrorMessage("")

    if (message.length === 0) {
      updateErrorMessage("Please enter a prompt.")
      return
    }

    onSubmit(message)
  }

  return (
    <section className={styles["chat-section"]}>
      <h1 className={styles["heading"]}>What would you like to automate today?</h1>
      <div className={styles["input-area"]}>
        <div className={classnames(styles["chat-field"], { [styles["with-errors"]]: !!errorMessage })}>
          <textarea
            ref={textAreaRef}
            placeholder="Describe what you'd like to build"
            rows={3}
            maxLength={500}
            value={prompt}
            onFocus={() => {
              if (errorMessage) updateErrorMessage("")
            }}
            onKeyDown={evt => {
              if (evt.keyCode === 13) {
                evt.preventDefault()
                onContinue()
              }
            }}
            onChange={evt => {
              setprompt(evt.target.value)
              if (errorMessage) updateErrorMessage("")
            }}
          />
          <div className={styles["actions"]}>
            <ContinueButton onContinue={onContinue} empty={prompt.length === 0} />
          </div>
        </div>
        {errorMessage && (
          <div className={styles["hint-helper"]}>
            <span className={styles["error-message"]}>
              <ErrorIcon width="24" height="24" />
              <span className={styles["badge"]}>{errorMessage}</span>
            </span>
          </div>
        )}
      </div>
      <div className={styles["examples"]}>
        <p>Try these examples</p>
        <ul>
          {examplePrompts.map((example, index) => (
            <li key={index}>
              <button
                onClick={() => {
                  setprompt(example)
                  updateErrorMessage("")
                }}
              >
                {example}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

const ContinueButton = ({ onContinue, empty }: { onContinue: () => void; empty: boolean }) => (
  <button id="continue" className={styles["start-building"]} onClick={onContinue} disabled={empty}>
    <span>Start building</span>
    <span className={styles["arrow-icon"]}>{parse(arrowRightIcon)}</span>
  </button>
)

export default Intro
